import { RadioGroup } from "@headlessui/react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { product } from "ramda";
import { useMemo, useState } from "react";

import CrossCountry from "~/types/cross-country";
import imageUtils from "~/utils/image-utils";
import productUtils from "~/utils/product-utils";

import Dialog from "../common/dialog";
import Icon from "../common/icon";
import ProductColorsDialog from "../products/product-colors-dialog";
import cartPickProductParentOptionDialogStyles from "../products/product-tile-dialog-desktop.module.scss";
import styles from "./cart-pick-product-option.module.scss";

type Props = {
  product: CrossCountry.ChildrenProduct & {
    children: CrossCountry.ChildrenProduct[];
  };
  currentSelection?: number | undefined;
  onSelect?: (productId: number) => void;
};

export default function CartPickProductParentOption(props: Props) {
  const t = useTranslations();
  const [isOpenColorSelector, setIsOpenColorSelector] = useState(false);
  const [selectedId, setSelectedId] = useState(() =>
    props.currentSelection && props.product.children.find((c) => c.product_id === props.currentSelection)
      ? props.currentSelection
      : props.product.children[0]?.product_id
  );
  const selectedProduct = props.product.children.find((c) => c.product_id === selectedId)!;

  const primary = selectedProduct?.product_media?.primary_image;
  const goccia = productUtils.getProductDroplet(selectedProduct?.product_media?.media);

  const sortedChildren = useMemo(() => {
    return props.product.children.sort((a, b) => {
      if (!props.currentSelection) {
        return 0;
      }

      if (a.product_id === props.currentSelection) {
        return -1;
      }

      if (b.product_id === props.currentSelection) {
        return 1;
      }

      return 0;
    });
  }, [props.product.children, props.currentSelection]);

  return (
    <div className={styles.container}>
      <div className={styles.imageContainer}>
        <Image
          src={primary?.url || imageUtils.transparentPixel}
          alt={props.product.display_value}
          width={80}
          height={80}
          loader={imageUtils.evaImageLoader}
        />
      </div>
      <div className={styles.infoContainer}>
        <p className={styles.productName}>{props.product.display_value}</p>

        <div className={styles.colorContainerWithChangeButton}>
          <div className={styles.wrapperColor}>
            <Image
              src={goccia?.url || imageUtils.transparentPixel}
              alt={selectedProduct?.color || ""}
              width={12}
              height={12}
              className={styles.colorImage}
              loader={imageUtils.evaImageLoader}
            />
            <p className={styles.colorName}>{selectedProduct?.color}</p>
          </div>
          <button
            type="button"
            className={styles.buttonChange}
            onClick={() => setIsOpenColorSelector(!isOpenColorSelector)}
          >
            {t("generic.change")}
          </button>
        </div>
        <Dialog
          isOpen={isOpenColorSelector}
          title={
            <>
              <p className={styles.actionsPaneSubTitle}>{selectedProduct?.color}</p>
              <p className={styles.actionsPaneTitle}>{t("generic.product_tiles.all_shades")}</p>
            </>
          }
          customStyles={cartPickProductParentOptionDialogStyles}
          onClose={() => setIsOpenColorSelector(false)}
        >
          <ProductColorsDialog
            products={sortedChildren}
            current={selectedProduct}
            isGift
            onChange={() => setIsOpenColorSelector(true)}
            onSelect={(product) => {
              props.onSelect?.(product.product_id);
              setSelectedId(product.product_id);
              setIsOpenColorSelector(false);
            }}
            onClose={() => setIsOpenColorSelector(false)}
            showColorList
          />
        </Dialog>
        {props.onSelect != undefined &&
          (props.currentSelection === selectedId ? (
            <div className={styles.checked}>
              <p>{t("generic.cart.selected_gift")}</p>
              <Icon name="done" width={16} height={16} />
            </div>
          ) : (
            <button className={styles.unchecked} onClick={() => setIsOpenColorSelector(true)}>
              <p>{t("generic.cart.unselected_gift")}</p>
            </button>
          ))}
      </div>
    </div>
  );
}
